import {ConnectorGenericOutputStructureType} from "@feature/services/connettori/types";

type Option = {label: string, value?: string, options?: Option[]};

/**
 * Builds a nested options structure from the output structure.
 *
 * @param structure - The output structure to convert.
 * @param separator - The separator to use for nested keys.
 * @param labelSeparator - The separator to use for nested labels.
 * @returns An array of options representing the output structure.
 */
export const buildOutputStructureOptions = (
  structure: Record<string, ConnectorGenericOutputStructureType>,
  separator: string = ".",
  labelSeparator: string = " > ",
): Option[] => {
  const flattenStructure = (
    struct: Record<string, ConnectorGenericOutputStructureType>,
    currentPath: string = ""
  ): Option[] => {
    const result: Option[] = [];

    const traverse = (
      obj: Record<string, ConnectorGenericOutputStructureType>,
      path: string
    ) => {
      for (const [key, value] of Object.entries(obj)) {
        const fullPath = path ? `${path}${separator}${key}` : key;

        if (value.type === "field" && path === "") {
          result.push({label: key, value: key});
          continue;
        }

        if (value.type === "object" || value.type === "list") {
          if (value.fields) {
            // Find all field-type children
            const fieldOptions = Object.entries(value.fields)
              .filter(([, childValue]) => childValue.type === "field")
              .map(([fieldKey, _]) => ({
                label: fieldKey,
                value: `${fullPath}${separator}${fieldKey}`
              }));

            if (fieldOptions.length > 0) {
              result.push({
                label: fullPath.split(separator).join(labelSeparator),
                options: fieldOptions
              });
            }

            // Recursively traverse
            traverse(value.fields, fullPath);
          }
        }
      }
    };

    traverse(struct, currentPath);
    return result;
  };

  return flattenStructure(structure);
};